/**
 * <TutorOffersPage> — l'onglet « Offres de cours » du masteur (page).
 *
 * Legacy `ProposalsTabScreen` (onglet `proposals`), à l'identique : le titre et la relecture, le
 * rappel « Confirme tes créneaux », les onglets (Groupes, Essais, Remplacements — ceux que ses droits
 * ouvrent), puis la liste des offres de l'onglet, dans l'ordre du serveur (les offres ciblées en tête),
 * page après page. Toucher une offre en attente l'ouvre, toucher une offre candidatée propose de
 * retirer la candidature : c'est `<TutorOfferFlow>`, que la page pose quand on le lui donne.
 *
 * - **Premier chargement** : le squelette de la page. **Changement d'onglet** : le haut reste, la
 *   liste montre trois offres squelettes (le legacy remontait la liste à vide). **Suite** : deux
 *   offres squelettes au pied. **Relecture** : la flèche tourne, rien d'autre.
 * - Sans aucun onglet (aucun droit), l'onglet n'existe pas dans la barre : la page ne se montre pas.
 *   Elle garde son titre seul si elle était tout de même montée, comme le legacy.
 *
 * Contrôlée : l'onglet, les offres, les envois en cours et la feuille ouverte arrivent en props.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton } from "../../atoms/Skeleton/DelayedSkeleton"
import { InfiniteList } from "../../molecules/InfiniteList"
import { TutorOfferCard, TutorOfferCardSkeleton } from "../../organisms/TutorOfferCard"
import { TutorOffersHeader } from "../../organisms/TutorOffersHeader"
import { PageTemplate } from "../../templates/PageTemplate"
import { TutorOfferFlow, type TutorOfferFlowProps } from "../../templates/TutorOfferFlow"
import { TutorOffersSkeleton } from "./TutorOffersSkeleton"
import {
    type UITutorOffer,
    type UITutorOfferKind,
    type UITutorOfferTab,
} from "../../../ports/primary/ui/types/tutorDispoOffres"

/** Une liste de cartes de texte : au-delà de 760 px, une ligne de créneaux devient trop longue à lire. */
export const TUTOR_LIST_WIDTH = 760

const TEXTS = { empty: "Aucune offre pour le moment." }

export type TutorOffersPageProps = {
    tabs: UITutorOfferTab[]
    activeKind: UITutorOfferKind
    onSelectTab: (kind: UITutorOfferKind) => void
    offers: UITutorOffer[]
    onPressOffer: (offer: UITutorOffer) => void
    /** Les offres dont la réponse part. */
    sendingIds?: number[]
    dispoStale?: boolean
    onOpenDispo?: () => void
    onRefresh?: () => void
    refreshing?: boolean
    /** L'onglet vient de changer : sa liste charge, le haut reste. */
    listLoading?: boolean
    loadingMore?: boolean
    hasMore?: boolean
    onLoadMore?: () => void
    loadMoreError?: string | null
    /** La feuille d'une offre, quand elle est ouverte. */
    flow?: TutorOfferFlowProps | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function TutorOffersPage(props: TutorOffersPageProps) {
    const {
        tabs,
        activeKind,
        onSelectTab,
        offers,
        onPressOffer,
        sendingIds = [],
        dispoStale = false,
        onOpenDispo,
        onRefresh,
        refreshing = false,
        listLoading = false,
        loadingMore = false,
        hasMore = false,
        onLoadMore,
        loadMoreError,
        flow,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    const header = (
        <View style={styles.header}>
            <TutorOffersHeader
                tabs={tabs}
                activeKind={activeKind}
                onSelectTab={onSelectTab}
                dispoStale={dispoStale}
                onOpenDispo={onOpenDispo}
                onRefresh={onRefresh}
                refreshing={refreshing}
            />
        </View>
    )

    return (
        <PageTemplate
            testID={testID}
            scroll={false}
            loading={loading}
            skeleton={<TutorOffersSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={TUTOR_LIST_WIDTH}
        >
            {tabs.length === 0 ? (
                header
            ) : listLoading ? (
                <Stack gap="0">
                    {header}
                    <DelayedSkeleton>
                        <Stack gap="4">
                            <TutorOfferCardSkeleton />
                            <TutorOfferCardSkeleton />
                            <TutorOfferCardSkeleton />
                        </Stack>
                    </DelayedSkeleton>
                </Stack>
            ) : (
                <InfiniteList
                    data={offers}
                    keyExtractor={(offer) => String(offer.id)}
                    renderItem={(offer) => (
                        <TutorOfferCard offer={offer} onPress={onPressOffer} sending={sendingIds.includes(offer.id)} />
                    )}
                    header={header}
                    emptyMessage={TEXTS.empty}
                    refreshing={refreshing}
                    onRefresh={onRefresh}
                    loadingMore={loadingMore}
                    moreSkeleton={
                        <Stack gap="4">
                            <TutorOfferCardSkeleton />
                            <TutorOfferCardSkeleton />
                        </Stack>
                    }
                    loadMoreError={loadMoreError}
                    onRetryLoadMore={onLoadMore}
                    hasMore={hasMore}
                    onEndReached={onLoadMore}
                />
            )}
            {flow != null && <TutorOfferFlow {...flow} />}
        </PageTemplate>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** L'écart entre le haut de la page et la première offre — celui des offres entre elles. */
    header: { paddingBottom: theme.spacing["4"] },
}))
